import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Paper } from '@mui/material';
import styles from './TAIChat.module.css';
import { ChatHeader } from './ChatHeader';
import { MessageList, ChatMessage } from './MessageList';
import { InputBar } from './InputBar';
import { buildTagSummarizationQuery, buildDisplayText, buildMediaCdnUrl } from './helpers';

const AI_API_BASE_URL = 'https://zbel1kblka.execute-api.us-east-1.amazonaws.com';
const AI_ENDPOINTS = {
  query: `${AI_API_BASE_URL}/query`,
  summarize: `${AI_API_BASE_URL}/summarize`,
  summarizePdf: `${AI_API_BASE_URL}/summarize-pdf`,
};

const CDN_BASE_URL = process.env.NEXT_PUBLIC_CDN || 'https://stage-cdn.staging.treedis.com';

export enum AIRequestType {
  GeneralQuery = 'generalQuery',
  TextSummarize = 'textSummarize',
  PdfSummary = 'pdfSummary',
  TagAnalysis = 'tagAnalysis'
}



export function ChatWindow({ tourSlug, onClose, moveToTag, tags }: {
  tourSlug: string;
  onClose: () => void;
  moveToTag?: (sid: string, doOpenTag?: boolean) => void;
  tags?: any[];
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const sessionId = useMemo(() => 'ai-chatbot-session', []);
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }));
  }, []);

  const send = useCallback(async () => {
    const userQuery = input.trim();
    if (!userQuery || loading) return;

    const userMsg: ChatMessage = { id: `${Date.now()}-u`, role: 'user', text: userQuery };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const requestQuery = `${userQuery}`;
      const res = await fetch(AI_ENDPOINTS.query, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userQuery: requestQuery, sessionId, tourSlug }),
      });
      const data = await res.json();

      const displayText = (typeof data?.userFriendlyExplanation === 'string' && data.userFriendlyExplanation.trim())
        ? data.userFriendlyExplanation
        : (typeof data?.summary === 'string' && data.summary.trim())
          ? data.summary
          : undefined;

      const assistantMsg: ChatMessage = {
        id: `${Date.now()}-a`,
        role: 'assistant',
        text: displayText ?? JSON.stringify(data ?? {}, null, 2),
        raw: data,
        query: typeof data?.query === 'string' ? data.query : null,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: any) {
      const errorMsg: ChatMessage = { id: `${Date.now()}-e`, role: 'system', text: `Error: ${err?.message || 'Failed to fetch'}` };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  }, [input, loading, sessionId, tourSlug, scrollToBottom]);

  const checkTagHasPdf = useCallback((tagId: string): boolean => {
    if (!tags || tags.length === 0) {
      return false;
    }

    const tag = tags.find((t: any) =>
      String(t.id) === String(tagId) ||
      String(t.sid) === String(tagId) ||
      String(t.defaultSid) === String(tagId)
    );

    if (!tag) {
      return false;
    }

    if (!tag.tagGalleries || !Array.isArray(tag.tagGalleries) || tag.tagGalleries.length === 0) {
      return false;
    }

    const hasPdf = tag.tagGalleries.some((gallery: any) => {
      const url = gallery?.url;
      return url && typeof url === 'string' && url.toLowerCase().endsWith('.pdf');
    });

    return hasPdf;
  }, [tags]);

  const handleAIRequest = useCallback(async (type: AIRequestType, data: any, displayText: string, query: string) => {
    if (loading) return;

    // Add user message
    const userMsg: ChatMessage = { id: `${Date.now()}-u`, role: 'user', text: displayText };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      let response: Response;

      switch (type) {
        case AIRequestType.GeneralQuery:
          // General AI query endpoint
          response = await fetch(AI_ENDPOINTS.query, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userQuery: query,
              sessionId: data.sessionId,
              tourSlug: data.tourSlug
            }),
          });
          break;

        case AIRequestType.TextSummarize:
          response = await fetch(AI_ENDPOINTS.summarize, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              text: data.text,
              query: query,
              sessionId: data.sessionId,
              maxLength: data.maxLength || 500,
              language: data.language || 'English'
            }),
          });
          break;

        case AIRequestType.PdfSummary:
          response = await fetch(AI_ENDPOINTS.summarizePdf, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              url: data.pdfUrl,
              tourSlug: data.tourSlug,
              sessionId: data.sessionId,
              maxLength: 500,
              language: 'English'
            }),
          });
          break;

        case AIRequestType.TagAnalysis:
          // Tag analysis endpoint (future)
          response = await fetch(`${AI_API_BASE_URL}/analyze-tags`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tagIds: data.tagIds,
              analysisType: data.analysisType,
              sessionId: data.sessionId,
              tourSlug: data.tourSlug
            }),
          });
          break;

        default:
          throw new Error(`Unknown request type: ${type}`);
      }

      const responseData = await response.json();

      const displayText = (typeof responseData?.userFriendlyExplanation === 'string' && responseData.userFriendlyExplanation.trim())
        ? responseData.userFriendlyExplanation
        : (typeof responseData?.summary === 'string' && responseData.summary.trim())
          ? responseData.summary
          : (typeof responseData?.response === 'string' && responseData.response.trim())
            ? responseData.response
            : undefined;

      const assistantMsg: ChatMessage = {
        id: `${Date.now()}-a`,
        role: 'assistant',
        text: displayText ?? JSON.stringify(responseData ?? {}, null, 2),
        raw: {
          ...responseData,
          ...(type === AIRequestType.GeneralQuery && query.toLowerCase().includes('pdf') && data.pdfUrl && {
            pdfUrl: data.pdfUrl,
            isPdfSummary: true
          })
        },
        query: typeof responseData?.query === 'string' ? responseData.query : null,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: any) {
      console.error(`Error in ${type} request:`, err);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-e`,
        role: 'system',
        text: `Error: ${err?.message || 'Failed to fetch'}`
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  }, [loading, sessionId, tourSlug, scrollToBottom]);

  const handleSummarizePdf = useCallback(async (tagId: string, tagTitle?: string) => {
    if (loading) return;

    const displayText = `Summarize tag ${tagTitle || tagId} PDF file`;

    const tag = tags?.find((t: any) =>
      String(t.sid) === String(tagId) ||
      String(t.id) === String(tagId) ||
      String(t.defaultSid) === String(tagId)
    );

    let pdfUrl = tag?.tagGalleries?.find((gallery: any) => {
      const url = gallery?.url;
      return url && typeof url === 'string' && url.toLowerCase().endsWith('.pdf');
    })?.url;

    if (!pdfUrl) {
      console.error('No PDF URL found for tag:', tagId);
      return;
    }

    pdfUrl = buildMediaCdnUrl(pdfUrl, CDN_BASE_URL);

    if (!pdfUrl.startsWith('http://') && !pdfUrl.startsWith('https://')) {
      console.error('Invalid PDF URL format:', pdfUrl);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-e`,
        role: 'system',
        text: 'Error: PDF URL must start with http:// or https://'
      };
      setMessages((prev) => [...prev, errorMsg]);
      return;
    }

    if (!pdfUrl.toLowerCase().endsWith('.pdf')) {
      console.error('URL does not point to a PDF file:', pdfUrl);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-e`,
        role: 'system',
        text: 'Error: URL must point to a PDF file'
      };
      setMessages((prev) => [...prev, errorMsg]);
      return;
    }

    const userMsg: ChatMessage = { id: `${Date.now()}-u`, role: 'user', text: displayText };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const requestBody = {
        url: pdfUrl,
        tourSlug,
        sessionId,
        maxLength: 500,
        language: 'English'
      };

      console.log('PDF Summarization Request:', requestBody);

      const res = await fetch(AI_ENDPOINTS.summarizePdf, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await res.json();
      console.log('PDF Summarization Response:', data);

      // Check if there's an error in the response
      if (data.error || !data.success) {
        throw new Error(data.error || 'PDF summarization failed');
      }

      const assistantMsg: ChatMessage = {
        id: `${Date.now()}-a`,
        role: 'assistant',
        text: data.summary || JSON.stringify(data, null, 2),
        raw: {
          ...data,
          pdfUrl,
          isPdfSummary: true
        },
        query: null,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: any) {
      console.error('Error in PDF summarization:', err);
      const errorMsg: ChatMessage = {
        id: `${Date.now()}-e`,
        role: 'system',
        text: `Error: ${err?.message || 'Failed to summarize PDF'}`
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
      scrollToBottom();
    }
  }, [loading, sessionId, tourSlug, tags, scrollToBottom]);

  useEffect(() => {
    if (messages.length === 0) {
      const welcomeMsg: ChatMessage = {
        id: 'welcome-msg',
        role: 'assistant',
        text: "Hey there! Welcome to TAI - your interactive AI assistant.",
        raw: {
          isWelcome: true,
          suggestions: [
            {
              label: "What I'm looking at",
              query: "What am I looking at?",
              type: AIRequestType.GeneralQuery,
              data: { tourSlug, sessionId }
            },
            {
              label: "List all tags",
              query: "List all tags",
              type: AIRequestType.GeneralQuery,
              data: { tourSlug, sessionId }
            },
            {
              label: "Show the most recent tag",
              query: "Show the most recent tag",
              type: AIRequestType.GeneralQuery,
              data: { tourSlug, sessionId }
            }
          ]
        }
      };
      setMessages([welcomeMsg]);
    }
  }, [messages.length, tourSlug, sessionId]);

  const handleSuggestionClick = useCallback((suggestion: any) => {
    if (loading) return;

    const displayText = suggestion.displayText || suggestion.query;

    handleAIRequest(
      suggestion.type,
      suggestion.data,
      displayText,
      suggestion.query
    );
  }, [loading, handleAIRequest]);

  const handleTagNavigation = useCallback((sid: string) => {
    moveToTag?.(sid, false);

    const tag = tags?.find((t: any) =>
      String(t.sid) === String(sid) ||
      String(t.id) === String(sid) ||
      String(t.defaultSid) === String(sid)
    );
    const tagTitle = tag?.title || `Tag ${sid}`;

    const hasDescription = tag?.description && tag.description.trim().length > 0;
    const hasTagGalleries = tag?.tagGalleries && Array.isArray(tag.tagGalleries) && tag.tagGalleries.length > 0;

    if (!hasDescription && !hasTagGalleries) {
      const simpleInfoMsg: ChatMessage = {
        id: `tag-nav-${Date.now()}`,
        role: 'assistant',
        text: `Navigated to ${tagTitle}. Tag has no description and no media`,
        raw: {
          isTagNavigation: true,
          isSimpleTag: true
        }
      };
      setMessages((prev) => [...prev, simpleInfoMsg]);
      scrollToBottom();
      return;
    }

    const tagInfo: string[] = [];
    if (tag?.title) tagInfo.push(`Title: ${tag.title}`);
    if (tag?.icon) tagInfo.push(`Icon: ${tag.icon}`);
    if (hasDescription) tagInfo.push(`Description: ${tag.description}`);

    if (hasTagGalleries) {
      const galleryInfo = tag.tagGalleries.map((gallery: any, idx: number) => {
        const parts = [];

        if (gallery.url) {
          const mediaUrl = buildMediaCdnUrl(gallery.url, CDN_BASE_URL);
          parts.push(`URL: ${mediaUrl}`);
        }
        if (gallery.type) parts.push(`Type: ${gallery.type}`);
        if (gallery.title) parts.push(`Title: ${gallery.title}`);
        if (gallery.description) parts.push(`Description: ${gallery.description}`);
        return `Media ${idx + 1}: ${parts.join(', ')}`;
      }).join('\n');
      tagInfo.push(`Media:\n${galleryInfo}`);
    }

    const tagText = tagInfo.length > 0
      ? tagInfo.join('\n\n')
      : `Tag: ${tagTitle}`;

    const pdfGallery = tag?.tagGalleries?.find((gallery: any) => {
      const url = gallery?.url;
      return url && typeof url === 'string' && url.toLowerCase().endsWith('.pdf');
    });
    const hasPdf = !!pdfGallery;
    const pdfUrl = pdfGallery ? buildMediaCdnUrl(pdfGallery.url, CDN_BASE_URL) : null;

    const query = buildTagSummarizationQuery({
      tagTitle,
      hasIcon: !!tag?.icon,
      hasMedia: hasTagGalleries,
      hasDescription
    });

    const suggestionMsg: ChatMessage = {
      id: `tag-nav-${Date.now()}`,
      role: 'assistant',
      text: `Navigated to ${tagTitle}`,
      raw: {
        isTagNavigation: true,
        hasPdf,
        pdfUrl,
        tagId: sid,
        tagTitle,
        tagSuggestion: {
          label: "Info about tag",
          query,
          displayText: buildDisplayText(tagTitle),
          type: AIRequestType.TextSummarize,
          data: {
            text: tagText,
            query,
            tagId: sid,
            tagTitle,
            sessionId,
            maxLength: 500,
            language: 'English'
          }
        }
      }
    };

    setMessages((prev) => [...prev, suggestionMsg]);
    scrollToBottom();
  }, [moveToTag, tags, sessionId, tourSlug, scrollToBottom]);

  return (
    <Paper className={styles.container} elevation={4} sx={{ background: '#FFF', borderRadius: 4 }}>
      <ChatHeader onClose={onClose} />
      <div ref={listRef} className={styles.messages}>
        <MessageList
          messages={messages}
          loading={loading}
          onClickTag={handleTagNavigation}
          checkTagHasPdf={checkTagHasPdf}
          onSummarizePdf={handleSummarizePdf}
          onSuggestionClick={handleSuggestionClick}
        />
      </div>
      <div className={styles.inputBar}>
        <InputBar
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onSubmit={send}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              send();
            }
          }}
          loading={loading}
        />
      </div>
      <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }} />
      <div className={styles.footerNote} style={{ padding: '8px 12px' }}>
        AI-powered chat. Verify important info.
      </div>
    </Paper>
  );
}

